import { ActivityReportType } from 'containers/activity-reports/ActivityReportFilter/ActivityReportFilter';
import useDashboardGraphDates from 'containers/dashboard/hooks/useDashboardGraphDates';
import { DashboardTimeFrame } from 'containers/dashboard/types/types';
import { useWeeklyPopulator } from 'hooks';
import { isNil, isNull, last } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { useFeatureFlags } from 'utils/features';

export default function useActivityReportDetailNavigation(
  timeFrame: DashboardTimeFrame,
  memberId?: string | null,
  costCodeId?: string | null,
  equipmentId?: string | null,
  projectId?: string | null
) {
  const { timeRange, timeRanges } = useDashboardGraphDates(timeFrame)!; // Null force safe for all settings but `clockStatus`

  const [timeRangeType, setTimeRangeType] = useState<TimeRangeType>(TimeRangeType.CUSTOM);
  const [navRange, setNavRange] = useState<ITimeRange<DateTime>>(timeRange);
  const currentTime = DateTime.utc().startOf('day');
  const isPro = useFeatureFlags('PRO');
  const weeklyRangeGet = useWeeklyPopulator();

  useEffect(() => {
    let newTimeRangeType = TimeRangeType.CUSTOM;
    switch (timeFrame) {
      case 'payPeriod':
        newTimeRangeType = TimeRangeType.PAY_PERIOD;
        break;
      case 'monthly':
        newTimeRangeType = TimeRangeType.MONTHLY;
        break;
      case 'weekly':
        newTimeRangeType = TimeRangeType.WEEKLY;
        break;
      default:
        newTimeRangeType = TimeRangeType.CUSTOM;
        break;
    }

    if (isPro) {
      setTimeRangeType(newTimeRangeType);

      let newNavRange = timeRange;
      if (timeFrame !== 'pastSeven' && timeFrame !== 'pastFourteen') {
        const lastRange = last(timeRanges);
        if (lastRange) {
          newNavRange = {
            startTime: DateTime.fromSeconds(lastRange.startTime, { zone: 'utc' }),
            endTime: DateTime.fromSeconds(lastRange.endTime, { zone: 'utc' }),
          };
        }
      }
      setNavRange(newNavRange);
    } else {
      setTimeRangeType(TimeRangeType.WEEKLY);
      const weeklyRange = last(weeklyRangeGet(1, currentTime.toSeconds())) ?? null;
      if (!isNull(weeklyRange)) {
        setNavRange({
          startTime: weeklyRange.startTime,
          endTime: weeklyRange.endTime,
        });
      }
    }
  }, [timeFrame]);

  function getActivityReportFilterType() {
    if (!isNil(memberId)) {
      return ActivityReportType.BY_EMPLOYEE;
    } else if (!isNil(costCodeId)) {
      return ActivityReportType.BY_COST_CODE;
    } else if (!isNil(equipmentId)) {
      return ActivityReportType.BY_EQUIPMENT;
    } else if (!isNil(projectId)) {
      return ActivityReportType.BY_PROJECT;
    }

    return ActivityReportType.BY_EMPLOYEE;
  }

  function getActivityReportFilter() {
    if (!isNil(memberId)) {
      return memberId;
    } else if (!isNil(costCodeId)) {
      return costCodeId;
    } else if (!isNil(equipmentId)) {
      return equipmentId;
    } else if (!isNil(projectId)) {
      return projectId;
    }

    return null;
  }

  return { timeRangeType, navRange, getActivityReportFilterType, getActivityReportFilter };
}
